import { useState } from 'react'
import { getStrings, type Lang } from '../i18n'

// Same primary CTA as nanorhino.com's Navbar: text START (US). Spanish keeps
// the channel picker page. 832 = dragonclaw line until 915 migrates.
const SMS_START_HREF = 'sms:+18326625181?body=START'
const coachHref = (lang: Lang) => (lang === 'es' ? 'https://nanorhino.com/message' : SMS_START_HREF)

function ArrowIcon() {
  return (
    <svg
      className="site-nav__cta-icon"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 12h14M13 6l6 6-6 6" />
    </svg>
  )
}

function ChatIcon() {
  return (
    <svg
      className="site-nav__cta-icon"
      width="16"
      height="16"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
      aria-hidden="true"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z"
      />
    </svg>
  )
}

export function SiteNav({
  mobileLegalLinks = false,
  lang = 'en',
}: { mobileLegalLinks?: boolean; lang?: Lang } = {}) {
  const t = getStrings(lang)
  const NAV_LINKS = t.nav.links
  // Shown in the hamburger menu only when `mobileLegalLinks` is set — mirrors
  // SiteFooter's Legal column.
  const LEGAL_LINKS = t.nav.legal
  const [mobileOpen, setMobileOpen] = useState(false)
  return (
    <nav className="site-nav" aria-label={t.nav.ariaPrimary}>
      <div className="site-nav__inner">
        <a href="https://nanorhino.com/" className="site-nav__brand">
          <img
            src={`${import.meta.env.BASE_URL}logo.jpg`}
            alt=""
            width={32}
            height={32}
            className="site-nav__logo"
          />
          <span className="site-nav__wordmark">NanoRhino</span>
        </a>

        <div className="site-nav__desktop">
          {NAV_LINKS.map((link) => (
            <a key={link.href} href={link.href} className="site-nav__link">
              {link.label}
            </a>
          ))}
          <a
            href={coachHref(lang)}
            className="site-nav__cta"
            aria-label={t.nav.coachAria}
          >
            {lang === 'es' ? <ChatIcon /> : <ArrowIcon />}
            {t.nav.coach}
          </a>
        </div>

        <button
          type="button"
          className="site-nav__hamburger"
          onClick={() => setMobileOpen((v) => !v)}
          aria-label={mobileOpen ? t.nav.closeMenu : t.nav.openMenu}
          aria-expanded={mobileOpen}
        >
          {mobileOpen ? (
            <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M6 18L18 6M6 6l12 12"
              />
            </svg>
          ) : (
            <svg width="24" height="24" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M4 6h16M4 12h16M4 18h16"
              />
            </svg>
          )}
        </button>
      </div>

      {mobileOpen && (
        <div className="site-nav__mobile">
          {NAV_LINKS.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="site-nav__link site-nav__link--mobile"
              onClick={() => setMobileOpen(false)}
            >
              {link.label}
            </a>
          ))}
          <a
            href={coachHref(lang)}
            className="site-nav__cta site-nav__cta--mobile"
            onClick={() => setMobileOpen(false)}
            aria-label={t.nav.coachAria}
          >
            {lang === 'es' ? <ChatIcon /> : <ArrowIcon />}
            {t.nav.coach}
          </a>
          {mobileLegalLinks && (
            <div className="site-nav__legal">
              {LEGAL_LINKS.map((link) => (
                <a
                  key={link.href}
                  href={link.href}
                  className="site-nav__legal-link"
                  onClick={() => setMobileOpen(false)}
                >
                  {link.label}
                </a>
              ))}
            </div>
          )}
        </div>
      )}
    </nav>
  )
}
